import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { createIdentitiesFetcher, useAutoFetch } from '../../contexts/Fetcher';
import { TextInputController } from '../InputControllers/TextInputController';
import { SelectController } from '../InputControllers/SelectController';
import { CheckboxController } from '../InputControllers/CheckboxController';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { apiCloudLive, ICreateLiveStackPayload } from '../../resources/api/apiCloudLive';
import { CustomerLiveFetcher } from '../../fetchers/CustomerLiveFetcher';
import { NotificationContext } from '../../contexts/NotificationContext';
import styled from '@mui/styles/styled';
import { useTranslation } from 'react-i18next';
import { LiveRegionContext } from '../../contexts/LiveRegionContext';
import { LiveRegionsFetcher } from '../../fetchers/LiveRegionsFetcher';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { apiOrganization } from '../../resources/api/apiOrganization';
import { UUID } from '../../types/uuid';

interface IProps {
  open: boolean;
  closeDialog: () => void;
}

const { useListOrganizationUsers } = apiOrganization;

const OrganizationUsersFetcher = createIdentitiesFetcher(() => {
  const { organization } = useContext(OrganizationContext);

  const { execute } = useListOrganizationUsers(organization?.name);

  return useCallback(() => {
    return execute({ page: 1, per: 1000 });
  }, [execute]);
});

interface IFormValues {
  userUuid: UUID;
  stackId: string;
  capacityReservation: boolean;
  allowListCIDR?: string;
}

const VerticallyCenteredDiv = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  paddingTop: theme.spacing(2),
}));

const { useCreateLiveStack } = apiCloudLive;

const CustomerNewLiveStackContent: FC<IProps> = ({ open, closeDialog }) => {
  const { onRefresh, data: stacksData } = useContext(CustomerLiveFetcher.Context);
  const { data: usersData } = useContext(OrganizationUsersFetcher.Context);
  useAutoFetch(OrganizationUsersFetcher, open);
  const { handleSubmit, control, formState, watch, setValue, clearErrors } = useForm({
    mode: 'onBlur',
  });
  const handleError = useApiErrorHandler();

  const { region } = useContext(LiveRegionContext);
  const { organization } = useContext(OrganizationContext);

  const { execute: createLiveStack } = useCreateLiveStack(organization ?? void 0, region);

  const { addNotification } = useContext(NotificationContext);
  const { t } = useTranslation();
  const { data: regions } = useContext(LiveRegionsFetcher.Context);

  const selectedUser = watch('userUuid');

  const onSubmit = useCallback(
    async (data: IFormValues) => {
      if (!organization) return null;

      const payload: ICreateLiveStackPayload = {
        ...data,
        organizationUuid: organization.id,
        protocol: 'SRT',
        subscriptionPlan: 1,
        cameraId: parseInt(data.stackId),
        allowListCIDR: data.allowListCIDR ?? '0.0.0.0/0',
      };
      try {
        await createLiveStack(payload);
        await onRefresh();
        addNotification({
          severity: 'success',
          message: t('organization.live.addSucceeded'),
        });
        closeDialog();
      } catch (e) {
        handleError(e);
      }
    },
    [createLiveStack]
  );

  const usersOptions = useMemo(() => {
    if (!usersData) return null;
    return usersData.data.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
    }));
  }, [usersData]);

  const usedStackIdsPerUser = useMemo(() => {
    if (!stacksData) return {};
    return stacksData.reduce((out, current) => {
      if (!current.userUuid) return out;

      return {
        ...out,
        [current.userUuid]: [...(out[current.userUuid] ?? []), current.cameraId],
      };
    }, {} as { [userUuid: string]: number[] });
  }, [stacksData]);

  useEffect(() => {
    const usedStackIds = usedStackIdsPerUser[selectedUser] ?? [0];
    const autoIncrement = Math.max(...usedStackIds) + 1;
    setValue('stackId', autoIncrement);
    clearErrors('stackId');
  }, [usedStackIdsPerUser, selectedUser]);

  const regionName = regions?.regions.find((r) => r.name === region)?.location ?? region;

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>{t('organization.live.addDialog.title')}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              {t('organization.live.addDialog.region', { name: regionName })}
            </Grid>
            <Grid item xs={6}>
              {usersOptions ? (
                <SelectController
                  defaultValue=""
                  control={control}
                  errors={formState.errors}
                  rules={{ required: true }}
                  label={t('organization.live.formLabel.user')}
                  name="userUuid"
                  items={usersOptions}
                />
              ) : null}
            </Grid>

            <Grid item xs={6}>
              <TextInputController
                defaultValue="1"
                control={control}
                errors={formState.errors}
                rules={{
                  required: true,

                  validate: (value) => {
                    if (usedStackIdsPerUser[selectedUser]?.includes(parseInt(value))) {
                      return t('organization.live.duplicatedStackId') as string;
                    }
                    return true;
                  },
                }}
                label={t('organization.live.formLabel.stackId')}
                inputType="number"
                InputProps={{
                  inputProps: {
                    min: 1,
                  },
                }}
                name="stackId"
              />
            </Grid>

            <Grid item xs={6}>
              <VerticallyCenteredDiv>
                <CheckboxController
                  defaultValue={false}
                  control={control}
                  errors={formState.errors}
                  label={t('organization.live.formLabel.capacityReservation')}
                  name="capacityReservation"
                />
              </VerticallyCenteredDiv>
            </Grid>

            <Grid item xs={12}>
              <TextInputController
                defaultValue=""
                control={control}
                errors={formState.errors}
                rules={{
                  required: true,
                  validate: (value) => {
                    const match = value.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/);
                    const invalidMsg = t('organization.live.addDialog.wrongCIDR');
                    if (!match) return invalidMsg;

                    const maxAllowed = [255, 255, 255, 255, 32];

                    if (maxAllowed.some((max, index) => match[index + 1] > max)) {
                      return invalidMsg;
                    }

                    return true;
                  },
                }}
                label={t('organization.live.formLabel.allowListCIDR')}
                inputType="text"
                InputProps={{
                  placeholder: '0.0.0.0/0',
                }}
                name="allowListCIDR"
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>
        <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)}>
          {t('organization.live.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CustomerNewLiveStack: FC<IProps> = (props) => {
  return (
    <OrganizationUsersFetcher.WMF>
      <CustomerNewLiveStackContent {...props} />
    </OrganizationUsersFetcher.WMF>
  );
};
